'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { useChat } from '@/lib/hooks/useChat';
import { ALL_MATCHES } from '@/lib/data/sharedData';
import { locauxService } from '@/lib/supabase/services';


// ─── Types ────────────────────────────────────────────────────────────────────

interface MatchResult {
  localRef: string;
  localTitre: string;
  localVille: string;
  localSurface: number;
  localLoyer: number;
  localFacade: number;
  localDisponibilite: string;
  enseigne: string;
  secteur: string;
  scoreGlobal: number;
  scoreLocalisation: number;
  scoreSurface: number;
  scoreFacade: number;
  scoreBudget: number;
  scoreTechnique: number;
  scoreTiming: number;
  statut: string;
  aiScore?: number;
  aiJustification?: string;
  aiScoreDetails?: AiScoreDetail[];
}

interface AiScoreDetail {
  critere: string;
  score: number;
  poids: number;
  justification: string;
}

interface CahierDesCharges {
  // Identité
  nomEnseigne: string;
  secteur: string;
  typeEtablissement: string;
  // Surfaces
  surfaceVenteMin: string;
  surfaceVenteMax: string;
  surfaceRDCMin: string;
  surfaceTotaleMax: string;
  // Localisation
  villesCibles: string[];
  typeEmplacement: string[];
  zonesExclues: string;
  centresCommerciaux: boolean;
  piedImmeuble: boolean;
  // Budget
  loyerMaxMensuel: string;
  droitEntreeMax: string;
  chargesMax: string;
  travauxPrisEnCharge: boolean;
  // Façade & Visibilité
  facadeMin: string;
  vitrine: boolean;
  angleRue: boolean;
  visibiliteAutoroute: boolean;
  // Contraintes techniques
  hauteurMin: string;
  puissanceElecMin: string;
  extractionAir: boolean;
  livraison: boolean;
  parking: boolean;
  accessibilitePMR: boolean;
  terrasse: boolean;
  cave: boolean;
  // Timing
  urgence: string;
  horizonOuverture: string;
  nbOuverturesPrevues: string;
  // Notes
  notesComplementaires: string;
}

interface AiMatchResult {
  localRef: string;
  scoreGlobal: number;
  scoreDetails: AiScoreDetail[];
  recommandation: string;
  pointsForts: string[];
  pointsFaibles: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MATCHES: MatchResult[] = ALL_MATCHES;

const statusColors: Record<string, string> = {
  'En discussion': 'bg-blue-50 text-blue-700 border-blue-200',
  'Contacté': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Planifié': 'bg-violet-50 text-violet-700 border-violet-200',
  'À contacter': 'bg-slate-50 text-slate-600 border-slate-200',
};

const SCORE_WEIGHTS = [
  { key: 'scoreLocalisation', label: 'Localisation', weight: 25, color: 'bg-blue-500' },
  { key: 'scoreSurface', label: 'Surface', weight: 20, color: 'bg-emerald-500' },
  { key: 'scoreFacade', label: 'Façade', weight: 15, color: 'bg-violet-500' },
  { key: 'scoreBudget', label: 'Budget', weight: 20, color: 'bg-amber-500' },
  { key: 'scoreTechnique', label: 'Technique', weight: 10, color: 'bg-rose-500' },
  { key: 'scoreTiming', label: 'Timing', weight: 10, color: 'bg-cyan-500' },
];

const SECTEURS = ['Mode & Textile', 'Restauration rapide', 'Café & Restauration', 'Décoration & Maison', 'Santé & Beauté', 'Grande distribution', 'Sport & Loisirs', 'Beauté & Cosmétiques', 'High-tech', 'Banque & Finance', 'Pharmacie', 'Optique', 'Bijouterie', 'Autre'];
const VILLES = ['Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès', 'Oujda', 'Kénitra', 'Tétouan'];
const TYPES_EMPLACEMENT = ['Pied d\'immeuble', 'Centre commercial', 'Zone commerciale', 'Retail park', 'Galerie marchande', 'Rue commerçante', 'Avenue principale'];
const TYPES_ETABLISSEMENT = ['Boutique', 'Restaurant', 'Café', 'Pharmacie', 'Supermarché', 'Grande surface', 'Showroom', 'Agence bancaire', 'Clinique', 'Autre'];

const defaultCDC: CahierDesCharges = {
  nomEnseigne: '', secteur: '', typeEtablissement: '',
  surfaceVenteMin: '', surfaceVenteMax: '', surfaceRDCMin: '', surfaceTotaleMax: '',
  villesCibles: [], typeEmplacement: [], zonesExclues: '',
  centresCommerciaux: false, piedImmeuble: false,
  loyerMaxMensuel: '', droitEntreeMax: '', chargesMax: '', travauxPrisEnCharge: false,
  facadeMin: '', vitrine: false, angleRue: false, visibiliteAutoroute: false,
  hauteurMin: '', puissanceElecMin: '', extractionAir: false, livraison: false,
  parking: false, accessibilitePMR: true, terrasse: false, cave: false,
  urgence: 'Normale', horizonOuverture: '', nbOuverturesPrevues: '1',
  notesComplementaires: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreCircle({ score, size = 'md' }: { score: number; size?: 'sm' | 'md' }) {
  const color = score >= 90 ? 'text-emerald-600 border-emerald-300 bg-emerald-50' :
    score >= 80 ? 'text-primary-700 border-primary-200 bg-primary-50' :
    score >= 70 ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-red-600 border-red-200 bg-red-50';
  const dim = size === 'sm' ? 'w-12 h-12 text-base' : 'w-16 h-16 text-xl';
  return (
    <div className={`${dim} rounded-full border-2 flex items-center justify-center flex-shrink-0 ${color}`}>
      <span className="font-700">{score}</span>
    </div>
  );
}

function AiScoreBadge({ score }: { score: number }) {
  const color = score >= 90 ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
    score >= 80 ? 'bg-blue-100 text-blue-700 border-blue-300' :
    score >= 70 ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-red-100 text-red-700 border-red-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-700 ${color}`}>
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      IA: {score}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MatchingContent() {
  // Existing matching state
  const [filterScore, setFilterScore] = useState(0);
  const [filterVille, setFilterVille] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'loyer' | 'surface' | 'ai'>('score');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // CDC form state
  const [showCDCForm, setShowCDCForm] = useState(false);
  const [cdcTab, setCdcTab] = useState('identite');
  const [cdc, setCdc] = useState<CahierDesCharges>(defaultCDC);

  // AI results state
  const [aiResults, setAiResults] = useState<Record<string, AiMatchResult>>({});
  const [aiAnalysisRunning, setAiAnalysisRunning] = useState(false);
  const [aiAnalysisDone, setAiAnalysisDone] = useState(false);

  // Real locaux from Supabase (with ai_extracted_data)
  const [supabaseLocaux, setSupabaseLocaux] = useState<any[]>([]);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Parse AI response when it arrives
  useEffect(() => {
    if (response && aiAnalysisRunning) {
      try {
        // Extract JSON from the response
        const jsonMatch = response.match(/```json\n?([\s\S]*?)\n?```/) || response.match(/(\{[\s\S]*\})/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[1]);
          if (parsed.resultats && Array.isArray(parsed.resultats)) {
            const resultsMap: Record<string, AiMatchResult> = {};
            parsed.resultats.forEach((r: AiMatchResult) => {
              resultsMap[r.localRef] = r;
            });
            setAiResults(resultsMap);
            setAiAnalysisRunning(false);
            setAiAnalysisDone(true);
            toast.success(`Analyse IA terminée — ${parsed.resultats.length} locaux scorés`);
          }
        }
      } catch {
        // Response might still be streaming, wait
      }
    }
  }, [response, aiAnalysisRunning]);

  const setField = (key: keyof CahierDesCharges, value: string | boolean | string[]) => {
    setCdc(f => ({ ...f, [key]: value }));
  };

  const toggleVille = (ville: string) => {
    setCdc(f => ({
      ...f,
      villesCibles: f.villesCibles.includes(ville)
        ? f.villesCibles.filter(v => v !== ville)
        : [...f.villesCibles, ville],
    }));
  };

  const toggleTypeEmplacement = (type: string) => {
    setCdc(f => ({
      ...f,
      typeEmplacement: f.typeEmplacement.includes(type)
        ? f.typeEmplacement.filter(t => t !== type)
        : [...f.typeEmplacement, type],
    }));
  };

  const handleLaunchAI = () => {
    if (!cdc.nomEnseigne || !cdc.secteur) {
      toast.error('Veuillez renseigner au minimum le nom de l\'enseigne et le secteur');
      return;
    }

    setAiAnalysisRunning(true);
    setAiAnalysisDone(false);

    // Build enriched locaux data: prefer Supabase rows (with ai_extracted_data), fall back to mock
    const locauxData = supabaseLocaux.length > 0
      ? supabaseLocaux.map((row: any) => {
          let aiData: any = null;
          try {
            aiData = row.ai_extracted_data
              ? (typeof row.ai_extracted_data === 'string' ? JSON.parse(row.ai_extracted_data) : row.ai_extracted_data)
              : null;
          } catch { aiData = null; }

          return {
            ref: row.ref,
            titre: row.title,
            ville: row.city,
            surface: Number(row.surface_total) || (aiData?.surfaceTotal ?? 0),
            loyer: Number(row.loyer) || (aiData?.loyer ?? 0),
            facade: Number(row.facade) || (aiData?.facade ?? 0),
            disponibilite: row.disponibilite || 'Inconnue',
            // AI-enriched fields
            aiKeywords: aiData?.matchingKeywords ?? [],
            aiSurfaceRDC: aiData?.surfaceRDC ?? null,
            aiFluxPieton: aiData?.fluxPieton ?? null,
            aiEtatLocal: aiData?.etatLocal ?? null,
            aiExtraction: aiData?.extraction ?? null,
            aiLivraison: aiData?.livraison ?? null,
            aiParking: aiData?.parking ?? null,
            aiHauteurPlafond: aiData?.hauteurPlafond ?? null,
            aiVoisinage: aiData?.voisinage ?? null,
          };
        })
      : MATCHES.map(m => ({
          ref: m.localRef,
          titre: m.localTitre,
          ville: m.localVille,
          surface: m.localSurface,
          loyer: m.localLoyer,
          facade: m.localFacade,
          disponibilite: m.localDisponibilite,
          aiKeywords: [],
        }));

    const systemPrompt = `Tu es un expert en immobilier commercial spécialisé dans le matching entre locaux commerciaux et enseignes/locataires au Maroc. 
Tu analyses les critères du cahier des charges d'une enseigne et tu scores chaque local disponible selon 6 critères pondérés.
Chaque local peut contenir des champs "aiKeywords", "aiSurfaceRDC", "aiFluxPieton", "aiEtatLocal", etc. extraits par IA depuis les textes libres du broker.
Ces données enrichissent le scoring : utilise-les pour détecter des compatibilités cachées (ex: mots-clés secteur, flux piéton fort pour restauration, extraction disponible pour food).
Tu réponds UNIQUEMENT en JSON valide, sans texte avant ou après, encapsulé dans des balises \`\`\`json ... \`\`\`.
Tous tes textes de justification sont en français.`;

    const userPrompt = `Analyse le cahier des charges suivant et score chaque local disponible.

## CAHIER DES CHARGES DE L'ENSEIGNE
- Nom: ${cdc.nomEnseigne}
- Secteur: ${cdc.secteur}
- Type d'établissement: ${cdc.typeEtablissement || 'Non précisé'}
- Surface de vente: ${cdc.surfaceVenteMin || '?'} à ${cdc.surfaceVenteMax || '?'} m²
- Surface RDC minimum: ${cdc.surfaceRDCMin || 'Non précisé'} m²
- Surface totale maximum: ${cdc.surfaceTotaleMax || 'Non précisé'} m²
- Villes cibles: ${cdc.villesCibles.length > 0 ? cdc.villesCibles.join(', ') : 'Toutes villes'}
- Types d'emplacement souhaités: ${cdc.typeEmplacement.length > 0 ? cdc.typeEmplacement.join(', ') : 'Tous types'}
- Zones exclues: ${cdc.zonesExclues || 'Aucune'}
- Loyer maximum mensuel: ${cdc.loyerMaxMensuel || 'Non précisé'} MAD
- Droit d'entrée maximum: ${cdc.droitEntreeMax || 'Non précisé'} MAD
- Charges maximum: ${cdc.chargesMax || 'Non précisé'} MAD
- Travaux pris en charge par bailleur: ${cdc.travauxPrisEnCharge ? 'Oui' : 'Non'}
- Façade minimum: ${cdc.facadeMin || 'Non précisé'} m
- Vitrine requise: ${cdc.vitrine ? 'Oui' : 'Non'}
- Angle de rue souhaité: ${cdc.angleRue ? 'Oui' : 'Non'}
- Hauteur minimum: ${cdc.hauteurMin || 'Non précisé'} m
- Puissance électrique minimum: ${cdc.puissanceElecMin || 'Non précisé'} kVA
- Extraction d'air requise: ${cdc.extractionAir ? 'Oui' : 'Non'}
- Livraison requise: ${cdc.livraison ? 'Oui' : 'Non'}
- Parking requis: ${cdc.parking ? 'Oui' : 'Non'}
- Accessibilité PMR: ${cdc.accessibilitePMR ? 'Oui' : 'Non'}
- Terrasse souhaitée: ${cdc.terrasse ? 'Oui' : 'Non'}
- Cave souhaitée: ${cdc.cave ? 'Oui' : 'Non'}
- Urgence: ${cdc.urgence}
- Horizon d'ouverture: ${cdc.horizonOuverture || 'Non précisé'}
- Nombre d'ouvertures prévues: ${cdc.nbOuverturesPrevues || '1'}
- Notes complémentaires: ${cdc.notesComplementaires || 'Aucune'}

## LOCAUX DISPONIBLES (avec données enrichies par IA)
${JSON.stringify(locauxData, null, 2)}

## INSTRUCTIONS DE SCORING
Pour chaque local, calcule un score global (0-100) basé sur ces 6 critères pondérés:
1. Localisation (25%): correspondance ville/zone avec les cibles
2. Surface (20%): adéquation surface avec les min/max du CDC — utilise aiSurfaceRDC si surface principale absente
3. Façade (15%): façade vs minimum requis
4. Budget (20%): loyer vs budget maximum
5. Technique (10%): contraintes techniques (extraction, livraison, parking, etc.) — utilise aiExtraction/aiLivraison/aiParking si disponibles
6. Timing (10%): disponibilité vs horizon d'ouverture **BONUS IA (jusqu'à +10 pts sur le score global)**: Si les aiKeywords du local correspondent au secteur de l'enseigne, ou si aiFluxPieton est "Très fort"/"Fort" pour un secteur à fort trafic (restauration, mode, beauté), ajouter des points bonus et le mentionner dans les justifications.

## FORMAT DE RÉPONSE ATTENDU
\`\`\`json
{
  "resultats": [
    {
      "localRef": "LOC-XXXX",
      "scoreGlobal": 85,
      "scoreDetails": [
        { "critere": "Localisation", "score": 90, "poids": 25, "justification": "..." },
        { "critere": "Surface", "score": 80, "poids": 20, "justification": "..." },
        { "critere": "Façade", "score": 85, "poids": 15, "justification": "..." },
        { "critere": "Budget", "score": 75, "poids": 20, "justification": "..." },
        { "critere": "Technique", "score": 90, "poids": 10, "justification": "..." },
        { "critere": "Timing", "score": 95, "poids": 10, "justification": "..." }
      ],
      "recommandation": "Texte de recommandation en français (2-3 phrases)",
      "pointsForts": ["Point fort 1", "Point fort 2"],
      "pointsFaibles": ["Point faible 1"]
    }
  ]
}
\`\`\``;

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ], { max_completion_tokens: 4000 });

    toast.info('Analyse IA en cours...', { duration: 3000 });
  };

  // Load real locaux from Supabase on mount
  useEffect(() => {
    locauxService.getAllFull().then(data => {
      if (data && data.length > 0) setSupabaseLocaux(data);
    }).catch(() => {
      // Silently fall back to mock data if Supabase unavailable
    });
  }, []);

  // Merge AI results into matches
  const enrichedMatches: MatchResult[] = MATCHES.map(m => {
    const aiResult = aiResults[m.localRef];
    if (aiResult) {
      return {
        ...m,
        aiScore: aiResult.scoreGlobal,
        aiJustification: aiResult.recommandation,
        aiScoreDetails: aiResult.scoreDetails,
      };
    }
    return m;
  });

  const filtered = enrichedMatches
    .filter(m => {
      const effectiveScore = m.aiScore ?? m.scoreGlobal;
      if (effectiveScore < filterScore) return false;
      if (filterVille && m.localVille !== filterVille) return false;
      if (filterStatut && m.statut !== filterStatut) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'ai') return (b.aiScore ?? b.scoreGlobal) - (a.aiScore ?? a.scoreGlobal);
      if (sortBy === 'score') return b.scoreGlobal - a.scoreGlobal;
      if (sortBy === 'loyer') return a.localLoyer - b.localLoyer;
      return b.localSurface - a.localSurface;
    });

  const avgScore = Math.round(filtered.reduce((s, m) => s + m.scoreGlobal, 0) / (filtered.length || 1));
  const avgAiScore = aiAnalysisDone
    ? Math.round(filtered.reduce((s, m) => s + (m.aiScore ?? 0), 0) / (filtered.filter(m => m.aiScore).length || 1))
    : null;

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide';

  const CDC_TABS = [
    { id: 'identite', label: 'Identité', icon: '🏷️' },
    { id: 'surfaces', label: 'Surfaces', icon: '📐' },
    { id: 'localisation', label: 'Localisation', icon: '📍' },
    { id: 'budget', label: 'Budget', icon: '💰' },
    { id: 'technique', label: 'Technique', icon: '⚙️' },
    { id: 'timing', label: 'Timing', icon: '📅' },
  ];

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Moteur de matching</h1>
          <p className="text-sm text-slate-500 mt-0.5">Correspondances intelligentes locaux ↔ enseignes · Scoring IA OpenAI</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCDCForm(!showCDCForm)}
            className={`btn-secondary flex items-center gap-2 ${showCDCForm ? 'bg-primary-50 border-primary-300 text-primary-700' : ''}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Cahier des charges
          </button>
          {aiAnalysisDone && (
            <button
              onClick={handleLaunchAI}
              disabled={isLoading}
              className="btn-secondary flex items-center gap-2 text-violet-700 border-violet-300 hover:bg-violet-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recalculer IA
            </button>
          )}
        </div>
      </div>

      {/* ── KPI row ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Paires analysées', value: MATCHES.length, icon: '🔗', color: 'text-primary-700' },
          { label: 'Score moyen', value: `${avgScore}/100`, icon: '⭐', color: 'text-amber-600' },
          { label: 'Score IA moyen', value: avgAiScore ? `${avgAiScore}/100` : '—', icon: '🤖', color: 'text-violet-600' },
          { label: 'Score ≥ 90', value: MATCHES.filter(m => m.scoreGlobal >= 90).length, icon: '🏆', color: 'text-emerald-600' },
        ].map(({ label, value, icon, color }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{icon}</span>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
            <p className={`text-2xl font-700 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* ── Cahier des Charges Form ── */}
      {showCDCForm && (
        <div className="bg-white rounded-2xl border border-primary-200 shadow-sm mb-6 overflow-hidden">
          {/* Form header */}
          <div className="bg-gradient-to-r from-primary-700 to-navy-800 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-700 text-white flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Cahier des charges — Matching IA
              </h2>
              <p className="text-xs text-primary-200 mt-0.5">Définissez les critères de l'enseigne pour lancer une analyse IA multi-critères</p>
            </div>
            <button onClick={() => setShowCDCForm(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 overflow-x-auto">
            {CDC_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setCdcTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-600 whitespace-nowrap border-b-2 transition-colors ${
                  cdcTab === tab.id
                    ? 'border-primary-700 text-primary-700 bg-primary-50/50' :'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-6">
            {/* Identité */}
            {cdcTab === 'identite' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div>
                  <label className={labelCls}>Nom de l'enseigne *</label>
                  <input className={inputCls} placeholder="Ex: Zara, McDonald's, Pharmacie..." value={cdc.nomEnseigne} onChange={e => setField('nomEnseigne', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Secteur d'activité *</label>
                  <select className={inputCls} value={cdc.secteur} onChange={e => setField('secteur', e.target.value)}>
                    <option value="">Sélectionner...</option>
                    {SECTEURS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Type d'établissement</label>
                  <select className={inputCls} value={cdc.typeEtablissement} onChange={e => setField('typeEtablissement', e.target.value)}>
                    <option value="">Sélectionner...</option>
                    {TYPES_ETABLISSEMENT.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Surfaces */}
            {cdcTab === 'surfaces' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <label className={labelCls}>Surface vente min (m²)</label>
                  <input type="number" className={inputCls} placeholder="Ex: 150" value={cdc.surfaceVenteMin} onChange={e => setField('surfaceVenteMin', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Surface vente max (m²)</label>
                  <input type="number" className={inputCls} placeholder="Ex: 500" value={cdc.surfaceVenteMax} onChange={e => setField('surfaceVenteMax', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Surface RDC min (m²)</label>
                  <input type="number" className={inputCls} placeholder="Ex: 100" value={cdc.surfaceRDCMin} onChange={e => setField('surfaceRDCMin', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Surface totale max (m²)</label>
                  <input type="number" className={inputCls} placeholder="Ex: 800" value={cdc.surfaceTotaleMax} onChange={e => setField('surfaceTotaleMax', e.target.value)} />
                </div>
              </div>
            )}

            {/* Localisation */}
            {cdcTab === 'localisation' && (
              <div className="space-y-5">
                <div>
                  <label className={labelCls}>Villes cibles</label>
                  <div className="flex flex-wrap gap-2">
                    {VILLES.map(v => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => toggleVille(v)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-500 border transition-all ${
                          cdc.villesCibles.includes(v)
                            ? 'bg-primary-700 text-white border-primary-700' :'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Types d'emplacement souhaités</label>
                  <div className="flex flex-wrap gap-2">
                    {TYPES_EMPLACEMENT.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTypeEmplacement(t)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-500 border transition-all ${
                          cdc.typeEmplacement.includes(t)
                            ? 'bg-emerald-600 text-white border-emerald-600' :'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Zones / quartiers exclus</label>
                    <input className={inputCls} placeholder="Ex: Zone industrielle, périphérie..." value={cdc.zonesExclues} onChange={e => setField('zonesExclues', e.target.value)} />
                  </div>
                  <div className="flex items-center gap-6 pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-primary-700" checked={cdc.centresCommerciaux} onChange={e => setField('centresCommerciaux', e.target.checked)} />
                      <span className="text-sm text-slate-700">Centres commerciaux acceptés</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-primary-700" checked={cdc.piedImmeuble} onChange={e => setField('piedImmeuble', e.target.checked)} />
                      <span className="text-sm text-slate-700">Pied d'immeuble accepté</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Budget */}
            {cdcTab === 'budget' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={labelCls}>Loyer max mensuel (MAD)</label>
                    <input type="number" className={inputCls} placeholder="Ex: 80000" value={cdc.loyerMaxMensuel} onChange={e => setField('loyerMaxMensuel', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Droit d'entrée max (MAD)</label>
                    <input type="number" className={inputCls} placeholder="Ex: 200000" value={cdc.droitEntreeMax} onChange={e => setField('droitEntreeMax', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Charges max mensuelles (MAD)</label>
                    <input type="number" className={inputCls} placeholder="Ex: 15000" value={cdc.chargesMax} onChange={e => setField('chargesMax', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Façade minimum (m)</label>
                    <input type="number" className={inputCls} placeholder="Ex: 8" value={cdc.facadeMin} onChange={e => setField('facadeMin', e.target.value)} />
                  </div>
                  <div className="flex items-center gap-6 pt-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-primary-700" checked={cdc.travauxPrisEnCharge} onChange={e => setField('travauxPrisEnCharge', e.target.checked)} />
                      <span className="text-sm text-slate-700">Travaux pris en charge par le bailleur</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-wrap gap-6">
                  {[
                    { key: 'vitrine', label: 'Vitrine requise' },
                    { key: 'angleRue', label: 'Angle de rue souhaité' },
                    { key: 'visibiliteAutoroute', label: 'Visibilité autoroute' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 accent-primary-700" checked={cdc[key as keyof CahierDesCharges] as boolean} onChange={e => setField(key as keyof CahierDesCharges, e.target.checked)} />
                      <span className="text-sm text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Technique */}
            {cdcTab === 'technique' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Hauteur sous plafond min (m)</label>
                    <input type="number" step="0.1" className={inputCls} placeholder="Ex: 3.5" value={cdc.hauteurMin} onChange={e => setField('hauteurMin', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Puissance électrique minimum (kVA)</label>
                    <input type="number" className={inputCls} placeholder="Ex: 60" value={cdc.puissanceElecMin} onChange={e => setField('puissanceElecMin', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Contraintes techniques requises</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: 'extractionAir', label: '🌬️ Extraction d\'air' },
                      { key: 'livraison', label: '🚚 Accès livraison' },
                      { key: 'parking', label: '🅿️ Parking' },
                      { key: 'accessibilitePMR', label: '♿ Accessibilité PMR' },
                      { key: 'terrasse', label: '☀️ Terrasse' },
                      { key: 'cave', label: '📦 Cave / réserve' },
                    ].map(({ key, label }) => (
                      <label key={key} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                        cdc[key as keyof CahierDesCharges]
                          ? 'bg-primary-50 border-primary-300 text-primary-700' :'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                        <input type="checkbox" className="w-4 h-4 accent-primary-700" checked={cdc[key as keyof CahierDesCharges] as boolean} onChange={e => setField(key as keyof CahierDesCharges, e.target.checked)} />
                        <span className="text-sm font-500">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Timing */}
            {cdcTab === 'timing' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={labelCls}>Urgence</label>
                    <select className={inputCls} value={cdc.urgence} onChange={e => setField('urgence', e.target.value)}>
                      <option>Normale</option>
                      <option>Urgente</option>
                      <option>Très urgente</option>
                      <option>Flexible</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Horizon d'ouverture</label>
                    <input className={inputCls} placeholder="Ex: T3 2026, Fin 2026..." value={cdc.horizonOuverture} onChange={e => setField('horizonOuverture', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Nb d'ouvertures prévues</label>
                    <input type="number" className={inputCls} placeholder="Ex: 3" value={cdc.nbOuverturesPrevues} onChange={e => setField('nbOuverturesPrevues', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Notes complémentaires</label>
                  <textarea className={`${inputCls} resize-none`} rows={4} placeholder="Informations supplémentaires, contraintes spécifiques, préférences particulières..." value={cdc.notesComplementaires} onChange={e => setField('notesComplementaires', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {/* Form footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between gap-4">
            <div className="text-xs text-slate-400">
              {cdc.nomEnseigne && cdc.secteur ? (
                <span className="text-emerald-600 font-600 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Prêt pour l'analyse IA — {cdc.nomEnseigne} · {cdc.secteur}
                </span>
              ) : (
                <span>Renseignez au minimum le nom et le secteur pour lancer l'analyse</span>
              )}
            </div>
            <button
              onClick={handleLaunchAI}
              disabled={isLoading || !cdc.nomEnseigne || !cdc.secteur}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyse en cours...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Lancer le matching IA
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── AI Analysis Banner ── */}
      {aiAnalysisDone && (
        <div className="bg-gradient-to-r from-violet-50 to-primary-50 border border-violet-200 rounded-xl p-4 mb-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-700 text-violet-900">Analyse IA terminée pour <span className="text-primary-700">{cdc.nomEnseigne}</span></p>
            <p className="text-xs text-violet-600 mt-0.5">{Object.keys(aiResults).length} locaux scorés · Scores IA affichés en violet · Cliquez sur un résultat pour voir les justifications détaillées</p>
          </div>
          <button onClick={() => setSortBy('ai')} className={`text-xs px-3 py-1.5 rounded-lg border font-600 transition-all ${sortBy === 'ai' ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-violet-700 border-violet-300 hover:bg-violet-50'}`}>
            Trier par score IA
          </button>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <label className="text-xs font-600 text-slate-500 whitespace-nowrap">Score min:</label>
          <input
            type="range" min={0} max={100} step={5} value={filterScore}
            onChange={e => setFilterScore(Number(e.target.value))}
            className="w-24 accent-primary-700"
          />
          <span className="text-xs font-700 text-primary-700 w-8">{filterScore}</span>
        </div>
        <select className="input-field w-auto min-w-36" value={filterVille} onChange={e => setFilterVille(e.target.value)}>
          <option value="">Toutes les villes</option>
          {['Casablanca', 'Rabat', 'Marrakech', 'Tanger', 'Fès'].map(v => <option key={v}>{v}</option>)}
        </select>
        <select className="input-field w-auto min-w-36" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {['En discussion', 'Contacté', 'Planifié', 'À contacter'].map(s => <option key={s}>{s}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs font-600 text-slate-500">Trier par:</label>
          <div className="flex bg-slate-100 rounded-lg p-1">
            {[
              { key: 'score', label: 'Score' },
              { key: 'ai', label: '🤖 IA' },
              { key: 'loyer', label: 'Loyer' },
              { key: 'surface', label: 'Surface' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSortBy(key as 'score' | 'loyer' | 'surface' | 'ai')}
                className={`px-3 py-1.5 text-xs font-600 rounded-md transition-all ${sortBy === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      <div className="space-y-3">
        <p className="text-sm text-slate-500">{filtered.length} correspondance{filtered.length !== 1 ? 's' : ''} trouvée{filtered.length !== 1 ? 's' : ''}</p>
        {filtered.map((m, i) => {
          const key = `${m.localRef}-${m.enseigne}`;
          const isExpanded = expandedId === key;
          const aiResult = aiResults[m.localRef];

          return (
            <div key={key} className={`bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-card-hover transition-shadow ${aiResult ? 'border-violet-200' : 'border-slate-200'}`}>
              {/* Main row */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : key)}
              >
                {/* Rank */}
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-700 text-slate-500 flex-shrink-0">
                  {i + 1}
                </div>
                {/* Score */}
                <ScoreCircle score={m.scoreGlobal} />
                {/* Local info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link href={`/locaux/1`} onClick={e => e.stopPropagation()} className="font-700 text-slate-900 text-sm hover:text-primary-700 transition-colors truncate">
                      {m.localTitre}
                    </Link>
                    <span className="text-slate-300">·</span>
                    <span className="text-xs text-slate-400 font-mono">{m.localRef}</span>
                    {aiResult && <AiScoreBadge score={aiResult.scoreGlobal} />}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span>{m.localVille}</span>
                    <span>·</span>
                    <span>{m.localSurface} m²</span>
                    <span>·</span>
                    <span>{m.localLoyer.toLocaleString('fr-FR')} MAD/mois</span>
                    <span>·</span>
                    <span>Façade {m.localFacade}m</span>
                  </div>
                </div>
                {/* Arrow */}
                <div className="flex items-center gap-1 text-slate-300 flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {/* Enseigne info */}
                <div className="flex-1 min-w-0 text-right">
                  <p className="font-700 text-slate-900 text-sm">{m.enseigne}</p>
                  <p className="text-xs text-slate-400">{m.secteur}</p>
                </div>
                {/* Status */}
                <span className={`status-badge border text-xs flex-shrink-0 ${statusColors[m.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {m.statut}
                </span>
                {/* Expand icon */}
                <svg className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/50 animate-fade-in">
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Standard scores */}
                    <div>
                      <p className="text-xs font-700 text-slate-600 uppercase tracking-wide mb-3">Scores algorithmiques</p>
                      <div className="space-y-2.5">
                        {SCORE_WEIGHTS.map(({ key: sk, label, weight, color }) => {
                          const val = m[sk as keyof MatchResult] as number;
                          return (
                            <div key={sk} className="flex items-center gap-3">
                              <span className="text-xs text-slate-500 w-24 flex-shrink-0">{label} ({weight}%)</span>
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${color}`} style={{ width: `${val}%` }} />
                              </div>
                              <span className="text-xs font-700 text-slate-700 w-8 text-right">{val}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* AI scores or actions */}
                    <div>
                      {aiResult ? (
                        <>
                          <p className="text-xs font-700 text-violet-700 uppercase tracking-wide mb-3 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Scores IA — {cdc.nomEnseigne}
                          </p>
                          <div className="space-y-2 mb-3">
                            {aiResult.scoreDetails?.map(d => (
                              <div key={d.critere}>
                                <div className="flex items-center gap-3 mb-0.5">
                                  <span className="text-xs text-slate-500 w-24 flex-shrink-0">{d.critere} ({d.poids}%)</span>
                                  <div className="flex-1 h-2 bg-violet-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-violet-500" style={{ width: `${d.score}%` }} />
                                  </div>
                                  <span className="text-xs font-700 text-violet-700 w-8 text-right">{d.score}</span>
                                </div>
                                <p className="text-xs text-slate-400 pl-28 leading-relaxed">{d.justification}</p>
                              </div>
                            ))}
                          </div>
                          {aiResult.recommandation && (
                            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-3">
                              <p className="text-xs font-600 text-violet-700 mb-1">Recommandation IA</p>
                              <p className="text-xs text-violet-800 leading-relaxed">{aiResult.recommandation}</p>
                            </div>
                          )}
                          {(aiResult.pointsForts?.length > 0 || aiResult.pointsFaibles?.length > 0) && (
                            <div className="grid grid-cols-2 gap-2">
                              {aiResult.pointsForts?.length > 0 && (
                                <div>
                                  <p className="text-xs font-600 text-emerald-700 mb-1">✅ Points forts</p>
                                  {aiResult.pointsForts.map((p, idx) => (
                                    <p key={idx} className="text-xs text-slate-600">• {p}</p>
                                  ))}
                                </div>
                              )}
                              {aiResult.pointsFaibles?.length > 0 && (
                                <div>
                                  <p className="text-xs font-600 text-amber-700 mb-1">⚠️ Points faibles</p>
                                  {aiResult.pointsFaibles.map((p, idx) => (
                                    <p key={idx} className="text-xs text-slate-600">• {p}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div>
                          <p className="text-xs font-700 text-slate-600 uppercase tracking-wide mb-3">Disponibilité & Actions</p>
                          <p className="text-sm font-600 text-slate-800 mb-4">{m.localDisponibilite}</p>
                          {!aiAnalysisDone && (
                            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-3">
                              <p className="text-xs text-violet-700">💡 Renseignez le cahier des charges et lancez l'analyse IA pour obtenir des scores détaillés avec justifications.</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-3">
                        <button
                          onClick={() => toast.success(`Visite planifiée pour ${m.enseigne}`)}
                          className="btn-primary text-xs px-3 py-1.5"
                        >
                          Planifier visite
                        </button>
                        <button
                          onClick={() => toast.success('Notification envoyée')}
                          className="btn-secondary text-xs px-3 py-1.5"
                        >
                          Notifier l'enseigne
                        </button>
                        <Link href={`/locaux/1`} className="btn-secondary text-xs px-3 py-1.5">
                          Voir le local →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
